import { useEffect } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { trackBudgetRequest, trackServiceView, trackWhatsappClick } from "@/lib/analytics";
import BreadcrumbTrail from "@/components/BreadcrumbTrail";
import { getLocalServicePageByPath } from "@/lib/local-service-pages";

const LocalServicePage = () => {
  const location = useLocation();
  const page = getLocalServicePageByPath(location.pathname);

  useEffect(() => {
    if (page) trackServiceView(page.path, { location: page.city });
  }, [page]);

  if (!page) return <Navigate to="/solucoes" replace />;

  return (
    <main id="main-content" className="bg-[#0a0a0a] pt-24">
      <section className="bg-[#ff6b35] noise py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <BreadcrumbTrail items={[{ label: "Soluções", href: "/solucoes" }, { label: page.service, href: page.path.includes("software") ? "/software-a-medida" : page.path.includes("automacao") ? "/automacao-empresarial" : "/criacao-de-sites" }, { label: page.city }]} />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-8 lg:gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-5">
                <MapPin size={14} className="text-[#ff6b35]" />
                <span>{page.city}</span>
              </div>
              <h1 className="font-display text-[#0a0a0a] text-[2.2rem] min-[380px]:text-[2.35rem] sm:text-7xl lg:text-8xl uppercase leading-[0.9]">
                {page.h1}
              </h1>
              <p className="mt-6 max-w-3xl text-[#0a0a0a]/80 text-lg sm:text-xl leading-snug">
                {page.localContext}
              </p>
              <a
                href={page.cta.href}
                onClick={() => {
                  trackBudgetRequest(`local_${page.path.replace(/\W+/g, "_")}`);
                  trackWhatsappClick(`local_${page.path.replace(/\W+/g, "_")}`);
                }}
                target="_blank"
                rel="noreferrer"
                className="btn-brutal mt-8 inline-flex"
              >
                <span>{page.cta.label}</span>
                <ArrowRight size={18} className="shrink-0" />
              </a>
            </div>

            <aside className="brutal-card bg-white p-6 sm:p-7">
              <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
                Serviço local
              </p>
              <p className="font-display text-[#0a0a0a] text-4xl uppercase leading-none">
                {page.service}
              </p>
              <p className="mt-5 text-[#0a0a0a]/75 leading-snug">{page.businessFit}</p>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-white py-18 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none">
              O que podemos implementar
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {page.services.map((service) => (
                <li key={service} className="bg-[#ff6b35] brutal-border px-4 py-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                  {service}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none">
              Perguntas frequentes
            </h2>
            <div className="mt-8 space-y-4">
              {page.faq.map((item) => (
                <article key={item.question} className="brutal-card bg-[#0a0a0a] text-white p-5">
                  <h3 className="font-display text-[#ff6b35] text-2xl uppercase leading-none">
                    {item.question}
                  </h3>
                  <p className="mt-3 text-white/75 leading-snug">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LocalServicePage;
